import {
  catchError,
  filter,
  map,
  switchMap,
  shareReplay,
  of,
  combineLatest,
  Observable,
  Subject,
  tap,
  startWith,
  distinctUntilChanged,
  timeout,
} from "rxjs";
import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { User } from "../../../shared/models/user.model";
import { toSignal } from "@angular/core/rxjs-interop";
import { UserSyncDbResult } from "../enums/user-sync-db-result.enum";
import { AuthService } from "./auth.service";
import { UserRole } from "../enums/role.enum";
import { LogService } from "../../../shared/service/logging/log.service";

@Injectable()
export class UserService {
  auth = inject(AuthService);
  http = inject(HttpClient);
  logger = inject(LogService);

  private userInfoRefresh$ = new Subject<void>();

  public readonly userInfo$: Observable<User | null> = combineLatest([
    this.auth.isLoggedIn$,
    this.auth.msfAccessToken$,
    this.auth.currentSessionAuth$,
    this.userInfoRefresh$.pipe(startWith(null)),
  ]).pipe(
    // filter(([isLoggedIn]) => !!isLoggedIn),
    switchMap(([isLoggedIn, token, isNewAuthSession]) => {
      if (!isLoggedIn) {
        this.logger.debug("User is logged out, emitting null");
        return of(null);
      }

      if (isNewAuthSession) {
        this.logger.debug("Calling performUserSync in UserService");
        return this.performUserSync(token).pipe(
          // Once the sync completes, fetch the user data.
          switchMap(() => this.fetchUserData()),
        );
      } else {
        return this.fetchUserData();
      }
    }),
    distinctUntilChanged(),
    tap(() => this.logger.debug("UserInfo emitted")),
    shareReplay(1),
    catchError((error) => {
      this.logger.error("Error fetching user data:", error);
      return of(this.getFallbackUser());
    }),
  );

  public readonly userInfo = toSignal<User>(this.userInfo$, {
    initialValue: undefined,
  });

  // Tiggers a user info refresh
  public refreshUserInfo() {
    this.logger.debug("refresh triggered");
    this.userInfoRefresh$.next();
  }

  public sendEmailConfirmation(): Observable<Object> {
    this.logger.debug("sending email confirmation");
    return this.http.post<any>("api/user/activate-account", {});
  }

  public sendResetPassword(email: string): Observable<Object> {
    this.logger.debug("sending reset password");
    return this.http.post<any>("api/user/reset-password", { email: email });
  }

  public initiateUserSync(
    msfAccessToken: string | null,
  ): Observable<UserSyncDbResult> {
    this.logger.debug("user sync triggered");

    return this.http
      .post<UserSyncDbResult>("api/user/sync", { msfAccessToken })
      .pipe(map((result: UserSyncDbResult) => this.processSyncResult(result)));
  }

  updateUsername(id: string, firstName: string, lastName: string) {
    return this.http.put(`api/user/${id}/username`, {
      firstName,
      lastName,
    });
  }

  getUser(id: string) {
    return this.http.get<{
      userId: string;
      firstName: string;
      lastName: string;
    }>(`api/user/${id}`);
  }

  private processSyncResult(result: UserSyncDbResult): UserSyncDbResult {
    this.logger.debug("userSyncResult: ", result);

    if (
      result === UserSyncDbResult.NoSyncNeeded ||
      result === UserSyncDbResult.UserCreated ||
      result === UserSyncDbResult.UserUpdated ||
      result === UserSyncDbResult.MappingsCreated
    ) {
    } else {
      this.handleSyncErrors(result);
    }

    return result;
  }

  private handleSyncErrors(result: UserSyncDbResult) {
    const errorMessages: Record<UserSyncDbResult, string> = {
      [UserSyncDbResult.NoSyncNeeded]: "",
      [UserSyncDbResult.UserCreated]: "",
      [UserSyncDbResult.UserUpdated]: "",
      [UserSyncDbResult.MappingsCreated]: "",
      [UserSyncDbResult.InvalidAuthUserId]: "Invalid auth userId provided.",
      [UserSyncDbResult.InvalidEmail]: "Invalid email address provided.",
      [UserSyncDbResult.Error]: "An unknown error occurred during sync.",
    };

    const message = errorMessages[result];
    if (message) {
      this.logger.error(message);
    }
  }

  private fetchUserData(): Observable<User> {
    return this.http.get<User>("api/user");
  }

  private getFallbackUser(): User {
    // TODO: Should a fallbackUser be a thing?
    return {
      userId: "",
      firstName: "fallbackFirstName",
      lastName: "fallbackLastName",
      email: "",
      phoneNumber: "",
      imageURL: "",
      city: null,
      coreMemberOfCommunities: [],
      memberOfCommunities: [],
      attendingEvents: [],
      roles: [UserRole.Anonymous],
      permissions: [],
    };
  }

  private performUserSync(
    msfAccessToken: string | null = null,
    timeoutInMs: number = 5000,
  ): Observable<UserSyncDbResult> {
    this.auth.redirectToCallback();
    this.logger.debug("msfAccessToken during userSync: ", !!msfAccessToken);
    return this.initiateUserSync(msfAccessToken).pipe(
      timeout(timeoutInMs),
      catchError((error) => {
        this.logger.error("Error during user sync or timeout occurred:", error);
        // Even if there’s an error, continue.
        return of(null);
      }),
      tap((result) => {
        if (result !== null) {
          this.auth.setSyncFlag(false);
        }
      }),
    );
  }
}
